/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { UpdateDto } from "@/lib/utility/data/update-dto.interface";
import productService from "@/services/mrfiktiv/services/product.service";
import {
  MrfiktivCreateProductDtoGen,
  MrfiktivProductViewModelGen,
  MrfiktivUpdateProductDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { ProductDataAccessLayer } from "@/store/modules/access-layers/product.access-layer";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { FormConfig, IsFormable } from "@/lib/formable";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { $t } from "@/lib/utility/t";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { TaxCategoryEnum } from "@/lib/enum/tax-category.enum";

/**
 * Indicates whether the item affects inventory or expense
 */
export enum ProductInventoryTypeEnum {
  /**
   * is the product hitting the inventory
   */
  STOCK = "stock",

  /**
   * is the product a direct expense
   */
  NON_STOCK = "non_stock",

  /**
   * is the product a service
   */
  SERVICE = "service"
}

export enum AccountingTypeEnum {
  EXPENSE = "expense",
  REVENUE = "revenue"
}

@IsFormable
@IsFilterable
class ProductBase
  implements IEntity<IProduct, MrfiktivUpdateProductDtoGen>, MrfiktivProductViewModelGen, UpdateDto<IProduct>
{
  _loading = false;

  isUpdateable = true;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.product.id"
  })
  id: string;
  partnerId: string;
  userId: string;
  timestamp: ITimestamp;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.title"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.title"
    }
  })
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.product.title"
  })
  title: string;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.currency"],
    type: DetailFormComponentsEnum.AUTO_COMPLETE,
    props: {
      items: Object.values(CurrencyEnum).map((v) => {
        return {
          text: $t(`enums.CurrencyEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      multiple: true,
      itemValue: "value",
      label: "objects.product.currency"
    },
    rules: [],
    clearable: true
  })
  @FilterConfig({
    displayName: "objects.currency.type",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(CurrencyEnum).map((v) => {
        return {
          text: $t(`enums.CurrencyEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  currency: CurrencyEnum;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.type"],
    type: DetailFormComponentsEnum.AUTO_COMPLETE,
    props: {
      items: Object.values(ProductInventoryTypeEnum).map((v) => {
        return {
          text: $t(`enums.ProductInventoryTypeEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      multiple: true,
      itemValue: "value",
      label: "objects.product.type"
    },
    rules: [],
    clearable: true
  })
  @FilterConfig({
    displayName: "objects.product.type",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(ProductInventoryTypeEnum).map((v) => {
        return {
          text: $t(`enums.ProductInventoryTypeEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  type: ProductInventoryTypeEnum;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.coverageType"],
    type: DetailFormComponentsEnum.AUTO_COMPLETE,
    props: {
      items: Object.values(CoverageTypeEnum).map((v) => {
        return {
          text: $t(`enums.CoverageTypeEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      multiple: true,
      itemValue: "value",
      label: "objects.product.coverageType"
    },
    rules: [],
    clearable: true
  })
  @FilterConfig({
    displayName: "objects.product.coverageType",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(CoverageTypeEnum).map((v) => {
        return {
          text: $t(`enums.CoverageTypeEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  coverageType?: CoverageTypeEnum | null;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.isActive"],
    type: DetailFormComponentsEnum.CHECK_BOX,
    props: {
      label: "objects.product.isActive"
    }
  })
  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.product.isActive"
  })
  isActive: boolean;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.description"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.description"
    }
  })
  description?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.hint"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.hint"
    }
  })
  hint?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.bookingText"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.bookingText"
    }
  })
  bookingText?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.externalId"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.externalId"
    }
  })
  externalId?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.code"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.code"
    }
  })
  code?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.amount"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.amount"
    }
  })
  amount?: number | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.unit"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.unit"
    }
  })
  unit?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.taxRate"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.taxRate"
    },
    clearable: true
  })
  taxRate?: number | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.taxCategory"],
    type: DetailFormComponentsEnum.AUTO_COMPLETE,
    props: {
      items: Object.values(TaxCategoryEnum).map((v) => {
        return {
          text: `${$t(`enums.TaxCategoryEnum.${v}`)} (${v})`,
          value: v
        } as IVSelectItem;
      }),
      multiple: true,
      itemValue: "value",
      label: "objects.product.taxCategory"
    },
    clearable: true
  })
  taxCategory?: TaxCategoryEnum | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.taxExemptionReason"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.taxExemptionReason"
    }
  })
  taxExemptionReason?: string | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.taxExemptionReasonCode"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.taxExemptionReasonCode"
    }
  })
  taxExemptionReasonCode?: string | null | undefined;

  @FormConfig({
    category: "billing",
    searchKeywords: ["objects.product.revenueAccount"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.revenueAccount"
    }
  })
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.product.revenueAccount"
  })
  revenueAccount?: number | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.expenseAccount"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.product.expenseAccount"
    }
  })
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.product.expenseAccount"
  })
  expenseAccount?: number | null | undefined;

  @FormConfig({
    category: "default",
    searchKeywords: ["objects.product.automaticAccount"],
    type: DetailFormComponentsEnum.CHECK_BOX,
    props: {
      label: "objects.product.expenseAccount"
    }
  })
  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.product.automaticAccount"
  })
  automaticAccount?: boolean | null | undefined;

  constructor(data?: Partial<MrfiktivProductViewModelGen> | IProduct) {
    this.id = data?.id ?? "";
    this.partnerId = data?.partnerId ?? "";
    this.userId = data?.userId ?? "";

    this.externalId = data?.externalId;
    this.code = data?.code;

    this.title = data?.title ?? "";
    this.description = data?.description;
    this.hint = data?.hint;
    this.bookingText = data?.bookingText;

    this.isActive = data?.isActive ?? true;

    this.currency = (data?.currency as CurrencyEnum) ?? CurrencyEnum.EUR;
    this.type = (data?.type as ProductInventoryTypeEnum) ?? ProductInventoryTypeEnum.SERVICE;
    this.unit = data?.unit;

    this.amount = data?.amount;
    this.taxRate = data?.taxRate;
    this.taxCategory = data?.taxCategory as TaxCategoryEnum;
    this.taxExemptionReason = data?.taxExemptionReason;
    this.taxExemptionReasonCode = data?.taxExemptionReasonCode;

    this.revenueAccount = data?.revenueAccount;
    this.expenseAccount = data?.expenseAccount;
    this.automaticAccount = data?.automaticAccount;

    this.coverageType = data?.coverageType as CoverageTypeEnum;

    this.timestamp = new Timestamp(data?.timestamp);
  }

  map(data?: Partial<MrfiktivProductViewModelGen> | IProduct) {
    this.id = data?.id ?? "";
    this.partnerId = data?.partnerId ?? "";
    this.userId = data?.userId ?? "";

    this.externalId = data?.externalId;
    this.code = data?.code;

    this.title = data?.title ?? "";
    this.description = data?.description;
    this.hint = data?.hint;
    this.bookingText = data?.bookingText;

    this.isActive = data?.isActive ?? true;

    this.currency = (data?.currency as CurrencyEnum) ?? CurrencyEnum.EUR;
    this.type = (data?.type as ProductInventoryTypeEnum) ?? ProductInventoryTypeEnum.SERVICE;
    this.unit = data?.unit;

    this.amount = data?.amount;
    this.taxRate = data?.taxRate;
    this.taxCategory = data?.taxCategory as TaxCategoryEnum;
    this.taxExemptionReason = data?.taxExemptionReason;
    this.taxExemptionReasonCode = data?.taxExemptionReasonCode;

    this.revenueAccount = data?.revenueAccount;
    this.expenseAccount = data?.expenseAccount;
    this.automaticAccount = data?.automaticAccount;

    this.coverageType = data?.coverageType as CoverageTypeEnum;

    this.timestamp = new Timestamp(data?.timestamp);
  }

  toLineItem(): IInvoiceLine {
    return new InvoiceLine({
      title: this.title,
      description: this.description ?? undefined,
      unit: this.unit ?? undefined,
      taxRate: this.taxRate ?? undefined,
      taxCategory: this.taxCategory ?? undefined,
      taxExemptionReason: this.taxExemptionReason ?? undefined,
      taxExemptionReasonCode: this.taxExemptionReasonCode ?? undefined,
      amount: this.amount ?? undefined,
      externalId: this.externalId ?? undefined
    });
  }

  async fetch(): Promise<this> {
    this._loading = true;

    try {
      const res = await productService.getOne(this.partnerId, this.id);
      this.map(res);

      return this;
    } finally {
      this._loading = false;
    }
  }

  async updatePartial(dto: Partial<MrfiktivUpdateProductDtoGen>): Promise<this> {
    this._loading = true;

    try {
      const res = await productService.update(this.partnerId, this.id, dto);
      this.map(res);
      ProductDataAccessLayer.set(this);
    } finally {
      this._loading = false;
    }

    return this;
  }

  async delete(): Promise<void> {
    this._loading = true;

    try {
      const res = await productService.delete(this.partnerId, this.id);
      this.map(res);
      ProductDataAccessLayer.set(this); // delete is not actually a delete but an "archive"
    } finally {
      this._loading = false;
    }
  }

  async update(): Promise<this> {
    this._loading = true;

    const data: MrfiktivUpdateProductDtoGen = {
      title: this.title,
      description: this.description,
      hint: this.hint,
      bookingText: this.bookingText,
      currency: this.currency,
      type: this.type,
      isActive: this.isActive,
      externalId: this.externalId,
      amount: this.amount ?? null,
      unit: this.unit,
      taxRate: this.taxRate ?? null,
      taxCategory: this.taxCategory ?? null,
      taxExemptionReason: this.taxExemptionReason ?? null,
      taxExemptionReasonCode: this.taxExemptionReasonCode ?? null,
      revenueAccount: this.revenueAccount ?? null,
      expenseAccount: this.expenseAccount ?? null,
      automaticAccount: this.automaticAccount,
      coverageType: this.coverageType
    };

    try {
      const res = await productService.update(this.partnerId, this.id, data);
      this.map(res);
      ProductDataAccessLayer.set(this);
    } finally {
      this._loading = false;
    }

    return this;
  }

  async create(): Promise<this> {
    this._loading = true;

    const data: MrfiktivCreateProductDtoGen = {
      title: this.title,
      description: this.description,
      hint: this.hint,
      bookingText: this.bookingText,
      currency: this.currency,
      type: this.type,
      isActive: this.isActive,
      externalId: this.externalId,
      amount: this.amount,
      unit: this.unit,
      taxRate: this.taxRate,
      taxCategory: this.taxCategory,
      taxExemptionReason: this.taxExemptionReason,
      taxExemptionReasonCode: this.taxExemptionReasonCode,
      revenueAccount: this.revenueAccount,
      expenseAccount: this.expenseAccount,
      automaticAccount: this.automaticAccount,
      coverageType: this.coverageType
    };

    try {
      const res = await productService.create(this.partnerId, data);
      this.map(res);

      ProductDataAccessLayer.set(this);
    } finally {
      this._loading = false;
    }

    return this;
  }
}

type IProduct = ProductBase;
const Product = Filter.createForClass(ProductBase);

export { IProduct, Product };
